export interface PackContents {
  itemId: string;
  quantity: number;
}

export interface EquipmentPack {
  id: string;
  name: string;
  cost: { amount: number; unit: 'gp' };
  contents: PackContents[];
  description: string;
}

/**
 * SRD 5.1 Equipment Packs
 * These are pre-made bundles of adventuring gear
 */
export const equipmentPacks: EquipmentPack[] = [
  {
    id: 'burglars-pack',
    name: "Burglar's Pack",
    cost: { amount: 16, unit: 'gp' },
    description: 'Designed for thieves and others who need to move quietly and access locked areas.',
    contents: [
      { itemId: 'backpack', quantity: 1 },
      { itemId: 'caltrops', quantity: 1 },
      { itemId: 'bell', quantity: 1 },
      { itemId: 'candle', quantity: 5 },
      { itemId: 'crowbar', quantity: 1 },
      { itemId: 'hammer', quantity: 1 },
      { itemId: 'piton', quantity: 10 },
      { itemId: 'lantern-hooded', quantity: 1 },
      { itemId: 'oil', quantity: 2 },
      { itemId: 'rations', quantity: 5 },
      { itemId: 'tinderbox', quantity: 1 },
      { itemId: 'waterskin', quantity: 1 },
      { itemId: 'rope-hempen', quantity: 1 },
    ],
  },
  {
    id: 'diplomats-pack',
    name: "Diplomat's Pack",
    cost: { amount: 39, unit: 'gp' },
    description: 'Suited for those who deal in negotiations and social encounters.',
    contents: [
      { itemId: 'chest', quantity: 1 },
      { itemId: 'case-map-scroll', quantity: 2 },
      { itemId: 'clothes-fine', quantity: 1 },
      { itemId: 'ink', quantity: 1 },
      { itemId: 'ink-pen', quantity: 1 },
      { itemId: 'lamp', quantity: 1 },
      { itemId: 'oil', quantity: 2 },
      { itemId: 'paper', quantity: 5 },
      { itemId: 'perfume', quantity: 1 },
      { itemId: 'sealing-wax', quantity: 1 },
      { itemId: 'soap', quantity: 1 },
    ],
  },
  {
    id: 'dungeoneers-pack',
    name: "Dungeoneer's Pack",
    cost: { amount: 12, unit: 'gp' },
    description: 'Essential gear for exploring dungeons and underground locations.',
    contents: [
      { itemId: 'backpack', quantity: 1 },
      { itemId: 'crowbar', quantity: 1 },
      { itemId: 'hammer', quantity: 1 },
      { itemId: 'piton', quantity: 10 },
      { itemId: 'torch', quantity: 10 },
      { itemId: 'tinderbox', quantity: 1 },
      { itemId: 'rations', quantity: 10 },
      { itemId: 'waterskin', quantity: 1 },
      { itemId: 'rope-hempen', quantity: 1 },
    ],
  },
  {
    id: 'entertainers-pack',
    name: "Entertainer's Pack",
    cost: { amount: 40, unit: 'gp' },
    description: 'Perfect for bards and performers.',
    contents: [
      { itemId: 'backpack', quantity: 1 },
      { itemId: 'bedroll', quantity: 1 },
      { itemId: 'clothes-costume', quantity: 2 },
      { itemId: 'candle', quantity: 5 },
      { itemId: 'rations', quantity: 5 },
      { itemId: 'waterskin', quantity: 1 },
      { itemId: 'disguise-kit', quantity: 1 },
    ],
  },
  {
    id: 'explorers-pack',
    name: "Explorer's Pack",
    cost: { amount: 10, unit: 'gp' },
    description: 'General-purpose gear for wilderness exploration.',
    contents: [
      { itemId: 'backpack', quantity: 1 },
      { itemId: 'bedroll', quantity: 1 },
      { itemId: 'mess-kit', quantity: 1 },
      { itemId: 'tinderbox', quantity: 1 },
      { itemId: 'torch', quantity: 10 },
      { itemId: 'rations', quantity: 10 },
      { itemId: 'waterskin', quantity: 1 },
      { itemId: 'rope-hempen', quantity: 1 },
    ],
  },
  {
    id: 'priests-pack',
    name: "Priest's Pack",
    cost: { amount: 19, unit: 'gp' },
    description: 'Equipment suitable for clerics and other religious characters.',
    contents: [
      { itemId: 'backpack', quantity: 1 },
      { itemId: 'blanket', quantity: 1 },
      { itemId: 'candle', quantity: 10 },
      { itemId: 'tinderbox', quantity: 1 },
      { itemId: 'rations', quantity: 2 },
      { itemId: 'waterskin', quantity: 1 },
      // Note: Also includes an alms box and 2 blocks of incense (not in standard gear)
    ],
  },
  {
    id: 'scholars-pack',
    name: "Scholar's Pack",
    cost: { amount: 40, unit: 'gp' },
    description: 'Equipment for wizards and others who value research and knowledge.',
    contents: [
      { itemId: 'backpack', quantity: 1 },
      { itemId: 'book', quantity: 1 },
      { itemId: 'ink', quantity: 1 },
      { itemId: 'ink-pen', quantity: 1 },
      { itemId: 'parchment', quantity: 10 },
      // Note: Also includes a little bag of sand and a small knife (not in standard gear)
    ],
  },
];

/**
 * Get equipment pack by ID
 */
export function getEquipmentPack(id: string): EquipmentPack | undefined {
  return equipmentPacks.find((p) => p.id === id);
}

/**
 * Calculate the total weight of a pack's contents
 * Note: This requires the adventuring gear data to be imported separately
 */
export function getPackContentsList(pack: EquipmentPack): string[] {
  return pack.contents.map((item) => {
    if (item.quantity > 1) {
      return `${item.itemId} (${item.quantity})`;
    }
    return item.itemId;
  });
}
